//Built in Libraries

//Third Part Libraries
import { Menu, Transition } from '@headlessui/react';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import {
  AdjustmentsVerticalIcon,
  ArrowRightOnRectangleIcon,
  BuildingLibraryIcon,
  QuestionMarkCircleIcon,
  UserCircleIcon,
} from '@heroicons/react/24/solid';
import { Fragment, useId, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router';
import NewTitleForm from '@/components/NewTitleForm/index.tsx';
import ChangeAccountForm from '@/components/Users/ChangeAccountForm';
import { NewCategoryForm } from '@/features/Category';
import { AppRoutes, buildRoute } from '@/route/routeConfig';
import useEnvironment from '../../../hooks/useEnvironment';
import { usePermission } from '../../../hooks/usePermission';
//Components
import { accountSelector, authSelector } from '../../../store';
import type { PermissionCommons } from '../../../types/Permission';
import { getInitials } from '../../../utils';
import { HasPermission } from '../../Permission/HasPermission';
import ImageCircularAvatar from '../../UI/Avatar/ImageCircularAvatar';
import PlusIcon from '../../UI/Icons/PlusIcon';
import Modal from '../../UI/Modal';
import SimpleDropDown, { DropDownItem } from '../../UI/SimpleDropDown';
import NewUserForm from '../../Users/NewUserForm';
import NewVendorForm from '../../Vendor/NewVendorForm';
import { Workspace } from '../../Workspace';
import Notifications from './Notifications';

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

interface GlobalAddOption {
  name: string;
  modalName: string;
  permission?: PermissionCommons;
}
const globalAddOptions: GlobalAddOption[] = [
  {
    name: 'Create New Project',
    modalName: 'createNewProject',
    permission: { of: 'CREATE_PROJECT' },
  },
  {
    name: 'Add New Title',
    modalName: 'addNewTitle',
    permission: { of: 'CREATE_TITLE' },
  },
  {
    name: 'Invite New User',
    modalName: 'inviteNewUser',
    permission: { of: 'INVITE_USER' },
  },
  {
    name: 'Invite New Vendor',
    modalName: 'inviteNewVendor',
  },
  {
    name: 'Add New Category',
    modalName: 'addNewCategory',
  },
];

export default function HeaderBar() {
  const navigate = useNavigate();
  const { data: currentUserData, currentDomain } = useSelector(authSelector);
  const { data: currentAccountData } = useSelector(accountSelector);
  const { hasPermission } = usePermission();
  const accountMenuId = useId();
  const [isModalOpen, setIsModalOpen] = useState<{ [key: string]: boolean }>({
    createNewProject: false,
    inviteNewUser: false,
    inviteNewVendor: false,
    addNewTitle: false,
    addNewCategory: false,
    changeAccount: false,
  });
  const currentEnv = useEnvironment();

  const modalHandler = (modalName: string) => {
    if (modalName === 'createNewProject') {
      //New project form is pretty big to display on the modal.
      //so, pushing it to another page
      return navigate(buildRoute(AppRoutes.SHERPA_PROJECTS_NEW));
    }

    //Handling all Modals needed in this component
    setIsModalOpen((prevState) => {
      return { ...prevState, [modalName]: !prevState[modalName] };
    });
  };

  const filterGlobalAddOptions = globalAddOptions.filter((item) => {
    if (currentUserData?.account === 'sageadmin' && (item.name === 'Create New Project' || item.name === 'Add New Category')) {
      return false;
    }
    return item;
  });

  return (
    <header className='w-full'>
      <div className='relative z-20 shrink-0 h-16 bg-white border-b border-gray-200 shadow-sm flex'>
        <div className='flex-1 flex justify-between px-4 sm:px-6 w-full'>
          <div className='flex'></div>
          <div className='ml-2 flex justify-end items-center space-x-4 sm:ml-6 sm:space-x-4 relative max-w-screen-sm w-full md:w-2/3 xl:w-2/5'>
            {currentUserData?.account !== 'sageadmin' && (
              <>
                <Workspace />
                <Notifications />
              </>
            )}

            {(currentAccountData.subdomain === 'chicagorebuild' || currentEnv.isDev) && (
              <span className={'text-neutralColor-normal hover:text-primaryColor-normal cursor-pointer'}>
                <Link to={buildRoute(AppRoutes.LEARN)}>
                  <QuestionMarkCircleIcon className='h-8 w-8' />
                </Link>
              </span>
            )}
            {isModalOpen.inviteNewUser && (
              <Modal>
                <NewUserForm formModalHandler={modalHandler} />
              </Modal>
            )}
            {isModalOpen.addNewTitle && (
              <Modal>
                <NewTitleForm closeForm={() => modalHandler('addNewTitle')} />
              </Modal>
            )}
            {isModalOpen.addNewCategory && <NewCategoryForm handleModal={() => modalHandler('addNewCategory')} />}
            {isModalOpen.inviteNewVendor && (
              <Modal>
                <NewVendorForm handleModal={() => modalHandler('inviteNewVendor')} />
              </Modal>
            )}
            {isModalOpen.changeAccount && (
              <Modal>
                <ChangeAccountForm handleModal={() => modalHandler('changeAccount')} />
              </Modal>
            )}
            {hasPermission(['CREATE_PROJECT', 'CREATE_TITLE', 'INVITE_USER', 'CREATE_CATEGORY']) && (
              <Menu as='div' className='relative shrink-0 mr-4'>
                <div>
                  <Menu.Button className='bg-sky-500 rounded-full flex text-sm focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-offset-sky-500 focus:ring-white'>
                    <span className='sr-only'>Open user menu</span>
                    <PlusIcon />
                  </Menu.Button>
                </div>
                <Transition
                  as={Fragment}
                  enter='transition ease-out duration-100'
                  enterFrom='transform opacity-0 scale-95'
                  enterTo='transform opacity-100 scale-100'
                  leave='transition ease-in duration-75'
                  leaveFrom='transform opacity-100 scale-100'
                  leaveTo='transform opacity-0 scale-95'
                >
                  <Menu.Items className='origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white ring-1 ring-black/5 focus:outline-hidden'>
                    {filterGlobalAddOptions.map((item) => {
                      const menuItem = (
                        <Menu.Item key={item.name}>
                          {({ active }) => {
                            return (
                              <span
                                className={classNames(
                                  active ? 'bg-green-50 border-l-4 border-green-500' : 'border-green-500/0',
                                  'block px-4 py-2 text-sm text-gray-700 cursor-pointer border-l-4'
                                )}
                                onClick={() => modalHandler(item.modalName)}
                              >
                                {item.name}
                              </span>
                            );
                          }}
                        </Menu.Item>
                      );
                      const { permission } = item;
                      if (permission) {
                        return (
                          <HasPermission key={item.name} of={permission.of}>
                            {menuItem}
                          </HasPermission>
                        );
                      }
                      return <div key={item.name}>{menuItem}</div>;
                    })}
                  </Menu.Items>
                </Transition>
              </Menu>
            )}
            <Menu as='div' className='relative shrink-0'>
              <div className='flex items-center gap-4'>
                <SimpleDropDown
                  menuButtonClassName='bg-white rounded-md flex items-center text-sm'
                  itemContainerWidth='w-72'
                  toggle={
                    <>
                      <span className='sr-only'>User profile</span>
                      <ImageCircularAvatar imageUrl={currentUserData.profileImageUrl} fallbackText={getInitials(currentUserData.name)} />
                    </>
                  }
                >
                  <DropDownItem id={`${accountMenuId}-account`} onClick={() => modalHandler('changeAccount')}>
                    <div className='flex justify-between w-full items-center'>
                      <h5 className='text-xs font-bold w-60 truncate'>
                        {currentUserData.membership.find((m) => m.subdomain === currentDomain)?.name}
                      </h5>
                      <ArrowTopRightOnSquareIcon className='w-4 h-4' />
                    </div>
                  </DropDownItem>
                  <DropDownItem id={`${accountMenuId}-name`} dropDownTitle>
                    <div className='truncate'>
                      <div className='flex gap-3 items-center'>
                        <ImageCircularAvatar imageUrl={currentUserData.profileImageUrl} fallbackText={getInitials(currentUserData.name)} />
                        <div>
                          <h3 className='text-sm font-bold truncate'>{currentUserData.name ? currentUserData.name : 'unknown user'}</h3>
                          <p className='text-xs text-neutralColor-normal truncate'>{currentUserData.title}</p>
                        </div>
                      </div>
                    </div>
                  </DropDownItem>
                  <hr className='my-2'></hr>
                  <DropDownItem id={`${accountMenuId}-profile`} onClick={() => navigate(buildRoute(AppRoutes.SETTINGS_PROFILE))}>
                    <div className='flex gap-2'>
                      <UserCircleIcon className='h-5 w-5' />
                      <span>Profile</span>
                    </div>
                  </DropDownItem>
                  <DropDownItem
                    id={`${accountMenuId}-notification-preferences`}
                    onClick={() => navigate(buildRoute(AppRoutes.SETTINGS_NOTIFICATIONS))}
                  >
                    <div className='flex gap-2'>
                      <AdjustmentsVerticalIcon className='h-5 w-5' />
                      <span>Notification Preference</span>
                    </div>
                  </DropDownItem>
                  {hasPermission(['READ_COMPANY_PROFILE']) && (
                    <DropDownItem id={`${accountMenuId}-company-profile`} onClick={() => navigate(buildRoute(AppRoutes.SETTINGS_COMPANY))}>
                      <div className='flex gap-2'>
                        <BuildingLibraryIcon className='h-5 w-5' />
                        <span>Company Profile</span>
                      </div>
                    </DropDownItem>
                  )}
                  <DropDownItem id={`${accountMenuId}-logout`} onClick={() => navigate('/logout')}>
                    <div className='flex gap-2'>
                      <ArrowRightOnRectangleIcon className='h-5 w-5' />
                      <span>Logout</span>
                    </div>
                  </DropDownItem>
                </SimpleDropDown>
              </div>
            </Menu>
          </div>
        </div>
      </div>
    </header>
  );
}
